import * as React from "react";
import Box from "@sproutsocial/seeds-react-box";
import Drawer, { DrawerContext } from "@sproutsocial/seeds-react-drawer";
import { useIsMobile } from "@sproutsocial/seeds-react-hooks";
import { Panel, PanelProvider } from "@sproutsocial/seeds-react-panel";
import { usePeekInContext } from "../PeekInContext";
import { RAIL_DEFAULT_WIDTH, RAIL_GAP } from "../constants";
import type { TypePeekInPanelProps } from "../PeekInTypes";

export const PeekInPanel: React.FC<TypePeekInPanelProps> = ({
  title,
  children,
  snapPoints,
  defaultSnapPoint,
  snapPoint,
  onSnapPointChange,
  snapToSequentialPoints,
  disableCloseOnClickOutside,
  actionsInHeader,
  headerless,
}) => {
  // `open` / `defaultOpen` / `onOpenChange` are deliberately not destructured
  // here — the parent PeekIn reads them off this element via
  // usePeekInPanelState and hands the resolved state back down through context.
  const { panelOpen, setPanelOpen, panelId } = usePeekInContext();
  const isMobile = useIsMobile();
  const parentDrawer = React.useContext(DrawerContext);

  // Lock the parent PeekIn Drawer while the panel is open so that Escape
  // closes the panel first, then the PeekIn on a second press.
  React.useEffect(() => {
    if (isMobile) parentDrawer.setIsLocked(panelOpen);
  }, [isMobile, panelOpen, parentDrawer]);

  // ── Mobile: nested bottom-sheet Drawer ────────────────────────────────────
  if (isMobile) {
    return (
      <Drawer
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        direction="bottom"
        closeButtonLabel="Close"
        snapPoints={snapPoints}
        defaultSnapPoint={defaultSnapPoint}
        snapPoint={snapPoint}
        onSnapPointChange={onSnapPointChange}
        snapToSequentialPoints={snapToSequentialPoints}
        disableCloseOnClickOutside={disableCloseOnClickOutside}
        actionsInHeader={actionsInHeader}
        aria-label={title ?? "Panel"}
      >
        {!headerless && <Drawer.Header title={title} />}
        <Drawer.Content>
          <Box
            id={panelId}
            role="region"
            aria-label={title}
            data-slot="peek-in-panel"
            data-qa-peek-in-panel
          >
            {children}
          </Box>
        </Drawer.Content>
      </Drawer>
    );
  }

  // ── Desktop: side panel (unchanged) ──────────────────────────────────────
  return (
    <PanelProvider open={panelOpen} onOpenChange={setPanelOpen}>
      <Panel
        closeButtonLabel="Close"
        direction="right"
        width={RAIL_DEFAULT_WIDTH}
        gap={RAIL_GAP}
        mobileBreakpoint={0}
        title={title}
        headerless={headerless}
        aria-label={title ?? "Panel"}
      >
        <Box
          id={panelId}
          role="region"
          aria-label={title}
          data-slot="peek-in-rail"
          data-qa-peek-in-rail
          flex="1 1 auto"
          overflow="auto"
        >
          {children}
        </Box>
      </Panel>
    </PanelProvider>
  );
};

PeekInPanel.displayName = "PeekInPanel";
